import { UploadOutlined } from '@ant-design/icons'
import { Button, Upload } from 'antd'
import { array, func, bool, object, string } from 'prop-types'
import React, { useState } from 'react'
import { errorMessage } from 'services/messager'
import { putObjectToS3 } from 'services/s3Upload'

const FileUpload = ({
	initialValue,
	metadata,
	onChange,
	folder,
	multiple,
	duplicateToContext,
}) => {
	const [fileList, setFileList] = useState(
		initialValue
			? (Array.isArray(initialValue) ? initialValue : [initialValue]).map(
					(file) => ({
						uid: file.uid,
						url: '',
						name: file.name,
						status: 'done',
					})
				)
			: []
	)

	const handleChange = (value) => {
		const files = value.fileList.map((file) => ({
			uid: file.uid,
			name: file.name,
		}))
		onChange(files)
		setFileList(value.fileList)
	}

	const beforeUpload = (file) => {
		const allowedTypes = multiple
			? [
					'application/pdf',
					'application/msword',
					'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
				]
			: ['application/pdf']

		const isAllowedType = allowedTypes.includes(file.type)
		if (!isAllowedType) {
			errorMessage({
				content: multiple
					? 'Anexos precisam ser PDFs, DOCs ou DOCXs!'
					: 'Anexos precisam ser PDFs!',
			})
		}

		const isLt10M = file.size / 1024 / 1024 < 10
		if (!isLt10M) {
			errorMessage({ content: 'O arquivo pode ter no máximo 10MB!' })
		}

		const lessThanMaxFiles = multiple
			? fileList.length < 10
			: fileList.length < 1
		if (!lessThanMaxFiles) {
			errorMessage({
				content: `Você pode anexar no máximo ${multiple ? 10 : 1} arquivo(s)!`,
			})
		}
		return isAllowedType && isLt10M && lessThanMaxFiles
			? true
			: Upload.LIST_IGNORE
	}

	const getFileData = (file, callback) => {
		const reader = new FileReader()
		reader.addEventListener('load', () => callback(reader.result))
		reader.readAsArrayBuffer(file)
	}

	const uploadToS3 = async ({
		data,
		file,
		onProgress,
		onSuccess,
		onError,
		extraPaths = [],
	}) => {
		try {
			const filePath = folder !== '' ? `${folder}/${file.uid}.pdf` : file.name
			const result = await putObjectToS3({
				key: filePath,
				body: data,
				contentType: file.type,
				metadata: metadata,
			})
			onProgress({ percent: 100 })
			for (const extraPath of extraPaths) {
				await putObjectToS3({
					key: extraPath,
					body: data,
					contentType: file.type,
					metadata: metadata,
				})
			}
			onSuccess(result, file)
		} catch (error) {
			console.log(error)
			errorMessage({ content: 'O upload do arquivo falhou!' })
			onError(error, file)
		}
	}

	const customRequestFunction = ({ file, onProgress, onSuccess, onError }) => {
		getFileData(file, (data) => {
			const extraPaths = []

			if (duplicateToContext) {
				extraPaths.push(`contexts/${file.uid}.pdf`)
			}

			uploadToS3({ data, file, onProgress, onSuccess, onError, extraPaths })
		})
	}

	const uploadProps = {
		name: 'attachments',
		customRequest: customRequestFunction,
		onChange: handleChange,
		multiple: multiple,
		fileList,
		beforeUpload,
		accept: multiple ? '.pdf,.doc,.docx' : '.pdf',
	}

	return (
		<Upload {...uploadProps}>
			<Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
		</Upload>
	)
}

FileUpload.propTypes = {
	initialValue: array,
	metadata: object,
	onChange: func.isRequired,
	folder: string,
	multiple: bool,
	duplicateToContext: bool,
}

FileUpload.defaultProps = {
	initialValue: [],
	metadata: {},
	folder: '',
	multiple: true,
	duplicateToContext: false,
}

export default FileUpload
